import React, { useState } from "react";
import { Box, Stack, Typography, Container, Modal, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { TrendingUp, Globe, Printer, Eye, X, ChevronDown } from "lucide-react";
import trendAnalysisImage from "../assets/trend analysis case 2 1.png";
import trendImage0 from "../assets/trends/Trend Analysis new (1)-pages-images-0.jpg";
import trendImage1 from "../assets/trends/Trend Analysis new (1)-pages-images-1.jpg";
import trendImage2 from "../assets/trends/Trend Analysis new (1)-pages-images-2.jpg";
import trendImage3 from "../assets/trends/Trend Analysis new (1)-pages-images-3.jpg";
import hindiSmartReportImage from "../assets/Hindi Smart report (2) 1.png";
import multilingualReportImage from "../assets/Multilingual_report_page.png";
import laptopPrinterImage from "../assets/laptop-wooden-table-with-printer 1.png";

const FeaturesContainer = styled(Box)({
  width: "100%",
  backgroundColor: "transparent",
  padding: "60px 0",
});

const FeatureCard = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  flex: 1,
  maxWidth: "340px",
  minHeight: "380px",
  backgroundColor: "#FFFFFF",
  borderRadius: "18px",
  padding: "24px",
  border: "1px solid rgba(13,38,76,0.06)",
  transition: "transform 220ms ease, box-shadow 220ms ease",
  [theme.breakpoints.down("md")]: {
    maxWidth: "100%",
    width: "100%",
    minHeight: "auto",
  },
}));


const PreviewButton = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(53, 103, 214, 0.1)',
  color: '#3567D6',
  borderRadius: '8px',
  padding: '6px 12px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '13px',
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  '&:hover': {
    backgroundColor: 'rgba(53, 103, 214, 0.2)',
    transform: 'scale(1.05)',
  },
}));

const PowerfulFeaturesSection = () => {
  const [openModal, setOpenModal] = useState(false);
  const [currentImages, setCurrentImages] = useState([]);
  const [currentTitle, setCurrentTitle] = useState("");

  const handleOpenModal = (images, title) => {
    setCurrentImages(images);
    setCurrentTitle(title);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCurrentImages([]);
    setCurrentTitle("");
  };

  const features = [
    {
      icon: <TrendingUp size={20} color="#3567D6" />,
      title: "Trend Report",
      titleColor: "#3567D6",
      description: "Compare historical values to track health progress effortlessly",
      image: trendAnalysisImage,
      previewImages: [trendImage0, trendImage1, trendImage2, trendImage3],
      showPreview: true,
    },
    {
      icon: <Globe size={20} color="#3567D6" />,
      title: "Multi-Language Options",
      titleColor: "#3567D6",
      description: "Access reports in the preferred language of your audience",
      image: hindiSmartReportImage,
      previewImages: [multilingualReportImage],
      showPreview: true,
    },
    {
      icon: <Printer size={20} color="#3567D6" />,
      title: "Print & Digital Versions",
      titleColor: "#3567D6",
      description: "Works seamlessly online and offline",
      image: laptopPrinterImage,
      previewImages: [],
      showPreview: false,
    },
  ];

  return (
    <FeaturesContainer>
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Heading */}
        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mb: 6 }}>
          <Typography
            sx={{
              color: "#1D47A4",
              fontFamily: "Poppins, sans-serif",
              fontSize: { xs: "32px", md: "40px" },
              fontWeight: 600,
              mr: 1,
              lineHeight: 1,
            }}
          >
            Powerful
          </Typography>
          <Typography
            sx={{
              color: "#1D47A4",
              fontFamily: "Poppins, sans-serif",
              fontSize: { xs: "32px", md: "40px" },
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            Features
          </Typography>
        </Stack>

        {/* Feature Cards */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={4}
          justifyContent="center"
          alignItems="stretch"
        >
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.03, boxShadow: "0 10px 30px rgba(13,38,76,0.08)" }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.18, type: "spring", stiffness: 120 }}
            >
              {/* Header with Icon and Preview Button */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%", mb: 2 }}>
                <Box
                  sx={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "8px",
                    backgroundColor: "#E3F2FD",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {feature.icon}
                </Box>

                {feature.showPreview && (
                  <PreviewButton onClick={() => handleOpenModal(feature.previewImages, feature.title)}>
                    <Eye size={16} />
                    Preview
                  </PreviewButton>
                )}
              </Box>

              {/* Title */}
              <Typography
                sx={{
                  color: feature.titleColor,
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "20px",
                  fontWeight: 700,
                  mb: 1.5,
                }}
              >
                {feature.title}
              </Typography>

              {/* Description */}
              <Typography
                sx={{
                  color: "#0f1724",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "15px",
                  fontWeight: 400,
                  lineHeight: "24px",
                  mb: 2.5,
                  flex: 1,
                }}
              >
                {feature.description}
              </Typography>

              {/* Feature Image */}
              <Box
                component="img"
                src={feature.image}
                alt={feature.title}
                sx={{
                  width: "100%",
                  height: "auto",
                  minHeight: { xs: "200px", sm: "180px", md: "160px" },
                  objectFit: "contain",
                  objectPosition: "center",
                  borderRadius: "10px",
                  border: "4px solid #F1F3F6",
                  mt: "auto",
                }}
              />
            </FeatureCard>
          ))}
        </Stack>
      </Container>

      {/* Modal for image preview - New UI */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="image-preview-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(12px)",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          sx={{
            width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
            maxWidth: "700px",
            maxHeight: { xs: "85vh", sm: "88vh", md: "90vh" },
            background: "#FFFFFF",
            borderRadius: { xs: "20px", md: "24px" },
            outline: "none",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 25px 80px rgba(0, 0, 0, 0.35)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: { xs: "16px 20px", md: "20px 28px" },
              borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
              background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFF 100%)",
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography
                id="image-preview-modal"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "20px", sm: "24px" },
                  fontWeight: 700,
                  color: "#1D47A4",
                  letterSpacing: "-0.02em",
                }}
              >
                {currentTitle}
              </Typography>
              {currentImages.length > 1 && (
                <Typography
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: { xs: "12px", md: "13px" },
                    fontWeight: 500,
                    color: "#6B7280",
                    mt: 0.5,
                  }}
                >
                  {currentImages.length} images
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                color: "#4674D9",
                backgroundColor: "rgba(70, 116, 217, 0.08)",
                width: { xs: "40px", md: "44px" },
                height: { xs: "40px", md: "44px" },
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "rgba(70, 116, 217, 0.15)",
                  transform: "rotate(90deg)",
                },
              }}
            >
              <X size={22} />
            </IconButton>
          </Box>

          {/* Image Body - Scrollable Gallery */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              padding: { xs: "16px", md: "24px" },
              background: "#F5F7FA",
              scrollBehavior: "smooth",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#E8EDF5",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "linear-gradient(180deg, #4674D9 0%, #6E91EB 100%)",
                borderRadius: "4px",
                "&:hover": {
                  background: "linear-gradient(180deg, #3563C8 0%, #5D80DA 100%)",
                },
              },
            }}
          >
            {currentImages.map((image, index) => (
              <Box
                key={index}
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
                sx={{
                  background: "#FFFFFF",
                  borderRadius: { xs: "12px", md: "16px" },
                  padding: { xs: "12px", md: "16px" },
                  marginBottom: index < currentImages.length - 1 ? { xs: "16px", md: "20px" } : 0,
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(70, 116, 217, 0.15)",
                  },
                }}
              >
                <Box
                  component="img"
                  src={image}
                  alt={`${currentTitle} - Image ${index + 1}`}
                  sx={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    borderRadius: { xs: "8px", md: "12px" },
                    display: "block",
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
                  }}
                />
              </Box>
            ))}
          </Box>

          {/* Scroll Indicator - only show if more than 1 image */}
          {currentImages.length > 1 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                padding: { xs: "10px 16px", md: "12px 20px" },
                background: "linear-gradient(180deg, #F8FAFF 0%, #FFFFFF 100%)",
                borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                color: "#6B7280",
                fontSize: { xs: "12px", md: "13px" },
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                flexShrink: 0,
                "& svg": {
                  animation: "bounce 1.5s infinite",
                },
                "@keyframes bounce": {
                  "0%, 100%": {
                    transform: "translateY(0)",
                  },
                  "50%": {
                    transform: "translateY(4px)",
                  },
                },
              }}
            >
              <ChevronDown size={18} />
              Scroll to view all images
            </Box>
          )}
        </Box>
      </Modal>
    </FeaturesContainer>
  );
};

export default PowerfulFeaturesSection;